import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Package } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SpinnerBlock } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
  listTikTokSampleRules,
  saveTikTokSampleRule,
  type TikTokAffiliateProduct,
  type TikTokSampleRule,
  type TikTokSampleRuleInput,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import { TikTokBlockedState } from "../TikTokBlockedState";
import { blockFromError, errorMessage, shortDate, usd } from "../tiktokFormat";

const RANKS: Array<{ value: NonNullable<TikTokSampleRuleInput["predictedFulfillmentRank"]>; label: string }> = [
  { value: "ALL", label: "Any creator" },
  { value: "LOW", label: "Low or better" },
  { value: "MEDIUM", label: "Medium or better" },
  { value: "HIGH", label: "High only" },
];

const STATUS_LABEL: Record<string, string> = {
  ONGOING: "Open",
  NOT_STARTED: "Starts later",
  NO_LEFT_COUNT: "All samples claimed",
  EXPIRED: "Ended",
  PLAN_EXCEPTION: "Collaboration issue",
};

function ruleSummary(rule: TikTokSampleRule | null): string {
  if (!rule?.active) return "Free samples off";
  const parts = [
    `${rule.availableQuantity ?? 0} of ${rule.quota ?? 0} left`,
    rule.minFollowers ? `${formatCompactCount(rule.minFollowers)}+ followers` : null,
    rule.minGmv ? `${usd(rule.minGmv)}+ GMV` : null,
    rule.timeUnlimited ? null : `until ${shortDate(rule.endTime)}`,
  ].filter(Boolean);
  return parts.join(" · ");
}

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

function RuleEditor({
  product,
  rule,
  onDone,
}: {
  product: TikTokAffiliateProduct;
  rule: TikTokSampleRule | null;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [active, setActive] = useState(rule?.active ?? true);
  const [quota, setQuota] = useState(String(rule?.quota ?? 20));
  const [minFollowers, setMinFollowers] = useState(rule?.minFollowers ? String(rule.minFollowers) : "");
  const [minGmv, setMinGmv] = useState(rule?.minGmv ? String(rule.minGmv) : "");
  const [minAvgViews, setMinAvgViews] = useState(rule?.minAvgViews ? String(rule.minAvgViews) : "");
  const [rank, setRank] = useState<NonNullable<TikTokSampleRuleInput["predictedFulfillmentRank"]>>(
    (rule?.predictedFulfillmentRank as never) ?? "ALL",
  );
  const [timeUnlimited, setTimeUnlimited] = useState(rule?.timeUnlimited ?? true);
  const [start, setStart] = useState(toDateInput(rule?.startTime ?? null) || new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(toDateInput(rule?.endTime ?? null));

  const num = (v: string) => (v.trim() ? Math.max(0, Math.round(Number(v))) : undefined);
  const save = useMutation({
    mutationFn: () =>
      saveTikTokSampleRule(
        product.id,
        active
          ? {
              active: true,
              quota: num(quota) ?? 1,
              minFollowers: num(minFollowers),
              minGmv: num(minGmv),
              minAvgViews: num(minAvgViews),
              predictedFulfillmentRank: rank,
              timeUnlimited,
              startTime: timeUnlimited ? undefined : new Date(`${start}T00:00:00`).toISOString(),
              endTime: timeUnlimited ? undefined : new Date(`${end}T23:59:59`).toISOString(),
            }
          : { active: false },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tiktok-sample-rules"] });
      toast.success(active ? `Free samples on for ${product.title}` : `Free samples off for ${product.title}`);
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="space-y-3 rounded-lg bg-muted/40 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm text-foreground">Offer free samples</Label>
        <Switch checked={active} onCheckedChange={setActive} />
      </div>
      {active && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Samples to give</Label>
              <Input inputMode="numeric" value={quota} onChange={(e) => setQuota(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Predicted to post</Label>
              <Select value={rank} onValueChange={(v) => setRank(v as never)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RANKS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Min followers</Label>
              <Input inputMode="numeric" placeholder="Any" value={minFollowers} onChange={(e) => setMinFollowers(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Min 30-day GMV ($)</Label>
              <Input inputMode="numeric" placeholder="Any" value={minGmv} onChange={(e) => setMinGmv(e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-normal text-muted-foreground">Min average shoppable video views</Label>
              <Input inputMode="numeric" placeholder="Any" value={minAvgViews} onChange={(e) => setMinAvgViews(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm font-normal text-foreground">Always open</Label>
            <Switch checked={timeUnlimited} onCheckedChange={setTimeUnlimited} />
          </div>
          {!timeUnlimited && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-normal text-muted-foreground">From</Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-normal text-muted-foreground">Until</Label>
                <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
          )}
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={save.isPending || (active && !timeUnlimited && !end)} onClick={() => save.mutate()}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/**
 * TikTok's per-product sample rules: which products offer free samples, how
 * many, and the creator thresholds TikTok checks before a request reaches
 * the brand. Only products in an open collaboration can carry one.
 */
export function SampleRulesSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const rules = useQuery({ queryKey: ["tiktok-sample-rules"], queryFn: listTikTokSampleRules, enabled: open });
  const [editing, setEditing] = useState<string | null>(null);
  const block = rules.error ? blockFromError(rules.error) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="text-left">
          <SheetTitle>Who can request samples</SheetTitle>
          <SheetDescription>
            TikTok shows a free-sample option on these products and only lets creators who meet your thresholds ask. Products appear here once they are in an open collaboration on TikTok.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-2">
          {rules.isLoading && <SpinnerBlock className="py-16" />}
          {block && <TikTokBlockedState block={block} feature="sample rules" onRetry={() => rules.refetch()} />}
          {rules.data?.length === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">No products in an open collaboration yet. Add products to open collaboration in TikTok Seller Center first.</p>
          )}
          {rules.data?.map(({ product, rule }) => (
            <div key={product.id} className="rounded-lg border border-border p-3">
              <div className="flex items-center gap-3">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt="" className="h-11 w-11 shrink-0 rounded-md object-cover" />
                ) : (
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Package className="h-4 w-4 text-muted-foreground" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{product.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {ruleSummary(rule)}
                    {rule?.active && rule.status && rule.status !== "ONGOING" ? ` · ${STATUS_LABEL[rule.status] ?? rule.status}` : ""}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {product.inventory !== null ? `${product.inventory.toLocaleString()} in stock` : ""}
                    {product.openCommission !== null ? ` · ${product.openCommission}% open commission` : ""}
                  </p>
                </div>
                {editing !== product.id && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setEditing(product.id)}>
                    {rule?.active ? "Edit" : "Set up"}
                  </Button>
                )}
              </div>
              {editing === product.id && (
                <div className="mt-3">
                  <RuleEditor product={product} rule={rule} onDone={() => setEditing(null)} />
                </div>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
